import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { Link } from 'react-router-dom'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import Header from '../../components/Header'
import Page from '../../components/Page'
import Paginator from '../../components/Paginator'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { useQsFilters } from '../../hooks/filters'
import { Produzione } from '../../types'
import { Field, Formik } from 'formik'
import { MultiSelectField, ReactSelectField } from '../../components/form'
import { useDeleteProduzione, useProduzioni, useUnificaProduzioni } from '../../hooks/produzioni'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? '',
})

export default function ProduzioniList() {
  const { filters, uiFilters, setFiltersDebounced, setFilters } =
    useQsFilters(initFilters)
  const { data } = useProduzioni(filters)
  const deleteProduzione = useDeleteProduzione()
  const [modalRm, modalRmActions] = useModalTrigger<Produzione>()
  const [modalUnifica, modalUnificaActions] = useModalTrigger()

  const filtersProduzioni = {
    page_size: 1000,
  }

  const { data: produzioniFull } = useProduzioni(filtersProduzioni)

  const produzioniOptions =
    produzioniFull?.results.map((prod) => ({
      value: prod.id,
      label: prod.nome,
    })) ?? []

  const unificaProduzioni = useUnificaProduzioni()

  return (
    <Page padded>
      <Header>
        <div className="d-flex align-items-end">
          <h2 className="m-0">Produzioni</h2>
          <div className="counter ms-2 mb-1">
            <small>
              {data!.count}
              {' / '}
              {data!.total_count}
            </small>
          </div>
        </div>
        <div className="d-flex">
          <Button
            color="primary"
            className="me-2"
            outline
            onClick={modalUnificaActions.toggle}
          >
            Unifica
          </Button>
          <Link className="btn btn-primary" to="/produzioni/new">
            <i className="bi bi-plus-circle me-2"></i> Aggiungi
          </Link>
        </div>
      </Header>
      <div className="d-flex justify-content-between mt-3 mb-3 align-items-center">
        <input
          placeholder="Cerca"
          className="form-control w-auto"
          type="text"
          value={uiFilters.search}
          onChange={(e) =>
            setFiltersDebounced({ search: e.target.value, page: 1 })
          }
        />
      </div>
      <StickyTable className="flex-1">
        <thead>
          <Sorter
            value={uiFilters.ordering}
            onSortChange={(ordering) =>
              setFilters({
                ordering,
                page: 1,
              })
            }
          >
            <tr>
              <th>#</th>
              <th>
                <div className="d-flex align-items-center">
                  Nome
                  <SortControl field="nome" />
                </div>
              </th>
            </tr>
          </Sorter>
        </thead>
        <tbody>
          {data!.results.map((prod) => (
            <tr key={prod.id}>
              <td>{prod.id}</td>
              <td>
                <Link to={`/produzioni/${prod.id}`}>{prod.nome}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </StickyTable>
      <Modal centered isOpen={modalRm.isOpen} toggle={modalRmActions.toggle}>
        <ModalHeader toggle={modalRmActions.toggle}>Elimina</ModalHeader>
        <ModalBody>
          {modalRm.value && (
            <div className="pt-3 pb-3">
              Sei sicuro di voler eliminare la produzione
              {modalRm.value.nome} ?
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <button
            onClick={modalRmActions.toggle}
            className="btn btn-outline-primary"
          >
            Annulla
          </button>
          <button
            className="btn  btn-danger"
            disabled={deleteProduzione.isLoading}
            onClick={async () => {
              await deleteProduzione.mutateAsync(modalRm.value!.id)
              modalRmActions.close()
            }}
          >
            <i className="bi bi-trash3-fill"></i> Elimina
          </button>
        </ModalFooter>
      </Modal>
      <Modal
        centered
        isOpen={modalUnifica.isOpen}
        toggle={modalUnificaActions.toggle}
      >
        <ModalHeader toggle={modalUnificaActions.toggle}>Unifica</ModalHeader>
        <Formik
          initialValues={{
            produzione_to_keep: null,
            produzioni_to_delete: [],
          }}
          onSubmit={async (values) => {
            if (
              !values.produzione_to_keep ||
              values.produzioni_to_delete.length === 0
            )
              return
            await unificaProduzioni
              .mutateAsync({
                produzione_to_keep: values.produzione_to_keep,
                produzioni_to_delete: values.produzioni_to_delete
              })
              .then(() => {
                modalUnificaActions.toggle()
              })
          }}
        >
          {({ values, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <ModalBody>
                <div>
                  <Field
                    component={ReactSelectField}
                    name="produzione_to_keep"
                    label="Produzione da mantenere"
                    placeholder="Seleziona la produzione da mantenere"
                    options={produzioniOptions}
                  />
                </div>
                <div>
                  <Field
                    component={MultiSelectField}
                    name="produzioni_to_delete"
                    placeholder="Seleziona le produzioni da eliminare"
                    label="Produzioni da eliminare"
                    options={produzioniOptions.filter(
                      (option) => option.value !== values.produzione_to_keep
                    )}
                    isMulti
                  />
                </div>
              </ModalBody>
              <ModalFooter className="d-flex justify-content-between">
                <button
                  onClick={modalUnificaActions.toggle}
                  className="btn btn-outline-primary"
                >
                  Annulla
                </button>
                <button className="btn btn-success" type="submit">
                  <i className="bi bi-save-fill me-1"></i>
                  Salva
                </button>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </Modal>
      <div className="position-relative">
        <Paginator
          count={data!.count}
          currentPage={uiFilters.page}
          goToPage={(page) => setFilters({ page })}
        />
      </div>
    </Page>
  )
}
